import { FC, ReactNode } from 'react'

import {
  Text,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyGrey90,
  Primary13Regular,
  Primary13SemiBold,
  Tooltip,
  Box,
  Flex
} from '@sequencehq/core-components'
import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import { formatTimestamp } from 'Automations/helpers'
import { inflect } from 'inflection'
import { useNodeTimelineVariant } from './NodeTimeline.atoms'
import { match } from 'ts-pattern'

const UNKNOWN_USER_LABEL: string = 'Unknown User'

export const TimelineUserAction: FC<{
  label: ReactNode
  performedBy?: string | Array<string>
  timestamp?: string
  inspectorDetail?: ReactNode
}> = ({ label, performedBy, timestamp, inspectorDetail }) => {
  const variant = useNodeTimelineVariant()

  const primaryUser = Array.isArray(performedBy) ? performedBy[0] : performedBy
  const otherUsers = Array.isArray(performedBy) ? performedBy.slice(1) : []

  const otherUsersTooltip = otherUsers.length > 0 && (
    <Tooltip
      label={
        <Box>
          {otherUsers.map(user => (
            <Box key={user}>{user || UNKNOWN_USER_LABEL}</Box>
          ))}
        </Box>
      }
    >
      <Text
        as="span"
        {...Primary13SemiBold}
        color={GreyGrey70}
        wordBreak="break-all"
      >
        {otherUsers.length} {inflect('other', otherUsers.length)}
      </Text>
    </Tooltip>
  )

  return match(variant)
    .with('DEFAULT', () => {
      return (
        <Flex gap="12px">
          <Flex flexWrap="wrap">
            <Text {...Primary13Regular} color={GreyGrey70} marginRight="8px">
              <Text as="span" {...Primary13SemiBold} color={GreyGrey80}>
                {label}
              </Text>
              {primaryUser ? ' by ' : ''}
              {primaryUser && (
                <Text
                  as="span"
                  {...Primary13SemiBold}
                  color={GreyGrey70}
                  wordBreak="break-all"
                >
                  {primaryUser || UNKNOWN_USER_LABEL}
                </Text>
              )}
              {otherUsers.length > 0 && (
                <>
                  {' or '}
                  {otherUsersTooltip}
                </>
              )}
              {!!timestamp && (
                <ErrorBoundary name="TimelineUserAction">
                  <Text
                    marginLeft="4px"
                    as="span"
                    {...Primary13Regular}
                    color={GreyGrey60}
                    whiteSpace="nowrap"
                  >
                    {formatTimestamp(timestamp)}
                  </Text>
                </ErrorBoundary>
              )}
            </Text>
          </Flex>
        </Flex>
      )
    })
    .with('INSPECTOR', () => {
      return (
        <Flex flexDir="column" gap="4px">
          <Text {...Primary13SemiBold} color={GreyGrey90}>
            {label}
          </Text>
          {primaryUser && (
            <Text
              {...Primary13Regular}
              color={GreyGrey60}
              wordBreak="break-all"
            >
              {primaryUser || UNKNOWN_USER_LABEL}
              {otherUsers.length > 0 && (
                <>
                  {' or '}
                  {otherUsersTooltip}
                </>
              )}
            </Text>
          )}
          {!!timestamp && (
            <ErrorBoundary name="TimelineUserAction">
              <Text {...Primary13Regular} color={GreyGrey60}>
                {formatTimestamp(timestamp)}
              </Text>
            </ErrorBoundary>
          )}
          {inspectorDetail}
        </Flex>
      )
    })
    .exhaustive()
}
